import { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { useAppStore } from "../../../../../../../GlobalStore/GlobalStore.ts";

interface MermaidHookResult {
    ref: React.RefObject<HTMLDivElement | null>;
    pngSrc: string | null;
    hasError: boolean;
    isRendering: boolean;
}

const getMermaidConfig = (isDarkMode: boolean) => ({
    startOnLoad: false,
    theme: isDarkMode ? 'dark' : 'default',
    securityLevel: 'loose' as const,
    logLevel: 0 as const,
    suppressErrorRendering: true,
    themeVariables: isDarkMode ? {
        primaryColor: '#262626',
        primaryBorderColor: '#404040',
        lineColor: '#404040',
        textColor: '#d4d4d4'
    } : undefined
});

const getSvgDimensions = (svgElement: SVGElement) => {
    const widthAttr = svgElement.getAttribute('width');
    const heightAttr = svgElement.getAttribute('height');
    const viewBox = svgElement.getAttribute('viewBox');
    const rect = svgElement.getBoundingClientRect();

    const parseDimension = (value: string | null) => {
        if (!value) return null;
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    if (rect.width > 0 && rect.height > 0) {
        return { width: rect.width, height: rect.height };
    }

    const width = parseDimension(widthAttr);
    const height = parseDimension(heightAttr);

    if (width && height) {
        return { width, height };
    }

    if (viewBox) {
        const [, , viewBoxWidth, viewBoxHeight] = viewBox
            .split(/[\s,]+/)
            .map((value) => Number.parseFloat(value));

        if (Number.isFinite(viewBoxWidth) && Number.isFinite(viewBoxHeight)) {
            return { width: viewBoxWidth, height: viewBoxHeight };
        }
    }

    return { width: 1200, height: 800 };
};

const svgElementToPng = async (svgElement: SVGElement): Promise<string> => {
    const { width, height } = getSvgDimensions(svgElement);
    const scale = window.devicePixelRatio > 1 ? 2 : 1;

    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        const image = new Image();
        const serializedSvg = new XMLSerializer().serializeToString(svgElement);
        const encodedSvg = encodeURIComponent(serializedSvg)
            .replace(/'/g, '%27')
            .replace(/"/g, '%22');

        canvas.width = Math.ceil(width * scale);
        canvas.height = Math.ceil(height * scale);

        image.onload = () => {
            if (!context) {
                reject(new Error('Failed to get canvas context'));
                return;
            }

            context.scale(scale, scale);
            context.drawImage(image, 0, 0, width, height);
            resolve(canvas.toDataURL('image/png'));
        };

        image.onerror = () => reject(new Error('Failed to load generated Mermaid SVG'));
        image.src = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
    });
};

export const useMermaid = (code: string): MermaidHookResult => {
    const elementRef = useRef<HTMLDivElement>(null);
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const isDarkMode = settingsObj.theme === 'dark';
    const [pngSrc, setPngSrc] = useState<string | null>(null);
    const [hasError, setHasError] = useState(false);
    const [isRendering, setIsRendering] = useState(false);
    const timeoutRef = useRef<number | undefined>(undefined);
    const renderAttemptRef = useRef(0);

    useEffect(() => {
        if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
        }

        const trimmedCode = code.trim();
        if (!trimmedCode) {
            setPngSrc(null);
            setHasError(false);
            setIsRendering(false);
            return;
        }

        setIsRendering(true);
        setHasError(false);

        const attemptId = ++renderAttemptRef.current;

        timeoutRef.current = window.setTimeout(async () => {
            try {
                const container = elementRef.current;
                if (!container || !container.isConnected) {
                    return;
                }

                mermaid.initialize(getMermaidConfig(isDarkMode));
                const { svg } = await mermaid.render(`mermaid-${attemptId}`, trimmedCode);
                container.innerHTML = svg;

                const svgElement = container.querySelector('svg');
                if (!svgElement) {
                    throw new Error('Failed to render Mermaid SVG');
                }

                const pngDataUrl = await svgElementToPng(svgElement);

                if (renderAttemptRef.current !== attemptId) {
                    return;
                }

                setPngSrc(pngDataUrl);
                setHasError(false);
            } catch {
                if (renderAttemptRef.current !== attemptId) {
                    return;
                }

                setPngSrc(null);
                setHasError(true);
            } finally {
                if (renderAttemptRef.current === attemptId) {
                    setIsRendering(false);
                }
            }
        }, 250);

        return () => {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
            }
        };
    }, [code, isDarkMode]);

    return {
        ref: elementRef,
        pngSrc,
        hasError,
        isRendering
    };
};
